import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';

const POSITIONS = [
  {
    icon: 'ri-route-line',
    title: '以时间线为核心',
    desc: '我们不追最快，只追最清楚。每条事件都按日期排成一条线，先发生什么、后来怎么变，一眼看得到。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '中立呈现多方说法',
    desc: '同一件事，不同人有不同说法，我们把它们并排列出，不替任何一方下结论。',
  },
  {
    icon: 'ri-live-line',
    title: '进度与结局持续维护',
    desc: '事件结束不等于时间线关闭。出现续集或反转，我们会重新点亮这条线并更新进度。',
  },
];

const PRINCIPLES = [
  '只收录可公开查证的时间与信息',
  '同一事件内的人物统一匿名化处理',
  '不发布未经核实的人身攻击性内容',
  '反转节点必须标注来源与发生日期',
  '结局更新后保留原有的全部节点',
];

const STEPS = [
  { step: '01', title: '收集线索', desc: '整理公开可查的信息，标注来源与时间。' },
  { step: '02', title: '核对时间', desc: '比对不同说法，确定每个节点发生的确切日期。' },
  { step: '03', title: '编排时间线', desc: '把信息拆成起始、后续、反转与结局四类节点。' },
  { step: '04', title: '持续更新', desc: '有新进展就追加节点，并同步刷新进度与状态。' },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于西瓜黑料',
        inLanguage: 'zh-CN',
        description: '西瓜黑料的品牌定位、内容原则与时间线整理方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
    [],
  );

  useSEO({
    title: '关于我们 — 西瓜黑料的时间线整理方式',
    description:
      '了解西瓜黑料是谁：我们以时间线方式整理网络热点，坚持中立呈现多方说法，持续维护每一出瓜的进度与结局，让吃瓜回归看事实。',
    keywords: '西瓜黑料,关于西瓜黑料,吃瓜时间线,内容原则',
    path: '/about',
    jsonLd,
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 pb-10 pt-24 md:px-8 md:pb-14 md:pt-28">
        <div className="mx-auto max-w-6xl">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            关于西瓜黑料
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们是一群喜欢把来龙去脉看清楚的吃瓜编辑。与其被碎片消息牵着走，不如把每件事按时间线排好，让你看完一整出戏再下判断。
          </p>
        </div>
      </section>

      <section className="px-4 py-14 md:px-8 md:py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              品牌介绍
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              把散落的消息，排成一条能读懂的线
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              西瓜黑料诞生于一个很朴素的困扰：一个热点从出现到收场，信息往往散落在几十个账号、上百条动态里。今天看一句、明天看一段，很难拼出完整的经过。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              于是我们决定换一种方式——把每件值得追的事，整理成一条带日期的时间线。起始、后续瓜、反转、续集与结局各就各位，顶部还有一条进度条，告诉你这出戏演到了哪一幕。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              站内所有人物与事件均经过创作化处理与匿名化表达，重点在于呈现信息结构，而不是评价任何真实的个人或机构。
            </p>
          </div>

          <div className="lg:col-span-6">
            <div className="w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=neat%20editorial%20desk%20with%20a%20timeline%20sketched%20on%20kraft%20paper%2C%20sticky%20notes%20in%20crimson%20and%20forest%20green%2C%20a%20cup%20of%20tea%2C%20warm%20morning%20light%2C%20stylized%20flat%20lay%20editorial%20photography%20with%20soft%20shadows&width=1000&height=750&seq=xg-about-desk&orientation=landscape"
                alt="编辑整理时间线的工作场景示意图"
                className="h-64 w-full object-top md:h-80"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-14 md:px-8 md:py-20">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            我们的定位
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {POSITIONS.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 md:px-8 md:py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              内容原则
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              这些规则写在我们的编辑手册第一页，每一条时间线上线前都会逐条过一遍。
            </p>
            <ul className="mt-6 space-y-3">
              {PRINCIPLES.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一条时间线是怎么诞生的
            </h2>
            <ol className="mt-8 space-y-5">
              {STEPS.map((item) => (
                <li key={item.step} className="flex gap-5">
                  <span className="font-heading text-2xl font-bold text-background-400">
                    {item.step}
                  </span>
                  <div className="border-l border-background-200 pl-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-accent-950 px-4 py-14 md:px-8 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-background-50">联系我们</h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200/80">
                如果你发现了值得追的线索，或想指出时间线中的时间与表述问题，欢迎写信告诉我们。我们会先核对，再决定是否更新。
              </p>
              <a
                href="mailto:contact@xigua-heiliao.example.com"
                rel="nofollow"
                className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
              >
                <i className="ri-mail-send-line"></i>
                contact@xigua-heiliao.example.com
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-lg border border-accent-800/70 bg-accent-900/50 p-6">
                <h3 className="font-heading text-base font-semibold text-background-50">
                  或许你还想看看
                </h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li>
                    <Link
                      to="/dramas"
                      className="flex items-center gap-2 text-background-200/85 transition-colors hover:text-accent-200"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                      瓜田总览：全部时间线
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      className="flex items-center gap-2 text-background-200/85 transition-colors hover:text-accent-200"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                      常见问题：关于时间线的一切
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/"
                      className="flex items-center gap-2 text-background-200/85 transition-colors hover:text-accent-200"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                      回到首页看今日热瓜
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}